/**
 * Progress Indicator Component
 * Phase 6: User Experience Enhancements - Progress Indicators
 */

import React from 'react';
import { FiCheckCircle, FiAlertTriangle, FiAlertCircle } from 'react-icons/fi';

interface ProgressIndicatorProps {
    current: number;
    max: number;
    type: 'gigs' | 'applications';
    unlimited?: boolean;
    className?: string;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
    current,
    max,
    type,
    unlimited = false,
    className = ''
}) => {
    if (unlimited) {
        return (
            <div className={`flex items-center space-x-2 ${className}`}>
                <FiCheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">
                    Unlimited Access
                </span>
            </div>
        );
    }

    const percentage = Math.min((current / max) * 100, 100);
    const remaining = max - current;
    const isNearLimit = percentage >= 80;
    const isAtLimit = current >= max;

    const getMessage = () => {
        const noun = type === 'gigs' ? 'gigs' : 'applications';
        const action = type === 'gigs' ? 'post' : 'apply to';

        if (isAtLimit) {
            return `Upgrade plan to continue ${type === 'gigs' ? 'posting' : 'applying'}`;
        } else if (isNearLimit) {
            return `${remaining} more ${remaining === 1 ? type.slice(0, -1) : noun} remaining - upgrade for unlimited`;
        } else {
            return `${remaining} ${remaining === 1 ? type.slice(0, -1) : noun} remaining`;
        }
    };

    const getProgressColor = () => {
        if (isAtLimit) return 'bg-red-500';
        if (isNearLimit) return 'bg-yellow-500';
        return type === 'gigs' ? 'bg-blue-600' : 'bg-green-600';
    };

    const getBackgroundColor = () => {
        return type === 'gigs' ? 'bg-blue-200 dark:bg-blue-800' : 'bg-green-200 dark:bg-green-800';
    };

    const getTextColor = () => {
        if (isAtLimit) return 'text-red-600 dark:text-red-400';
        if (isNearLimit) return 'text-yellow-600 dark:text-yellow-400';
        return type === 'gigs' ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400';
    };

    const getIcon = () => {
        if (isAtLimit) return <FiAlertCircle className="w-4 h-4" />;
        if (isNearLimit) return <FiAlertTriangle className="w-4 h-4" />;
        return null;
    };

    return (
        <div className={`space-y-2 ${className}`}>
            {/* Progress Bar */}
            <div className="space-y-1">
                <div className="flex justify-between text-xs">
                    <span className={getTextColor()}>{current}/{max} used</span>
                    <span className={getTextColor()}>{Math.round(percentage)}%</span>
                </div>
                <div className={`w-full rounded-full h-2 ${getBackgroundColor()}`}>
                    <progress
                        max={100}
                        value={percentage}
                        className={`w-full h-2 rounded-full overflow-hidden [&::-webkit-progress-bar]:bg-transparent [&::-moz-progress-bar]:bg-current [&::-webkit-progress-value]:bg-current ${getProgressColor().replace('bg-', 'text-')}`}
                        aria-label={`${type} usage progress`}
                    />
                </div>
            </div>

            {/* Progressive Messaging */}
            <div className={`flex items-start space-x-2 text-xs font-medium ${getTextColor()}`}>
                {getIcon()}
                <span>{getMessage()}</span>
            </div>

            {/* Upgrade Plan Prompt */}
            {(isNearLimit || isAtLimit) && (
                <div className="text-xs">
                    <span className={getTextColor()}>
                        💡 Upgrade plan to unlock unlimited access
                    </span>
                </div>
            )}
        </div>
    );
};

export default ProgressIndicator;